import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Autocomplete, TextField
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const fmt = (n) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 }).format(Number(n || 0));
const fmtN = (n) => new Intl.NumberFormat('es-PE', { minimumFractionDigits: 2 }).format(Number(n || 0));

function KpiCard({ icon, label, value, sub, color = '#1976d2' }) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box sx={{ bgcolor: color + '18', borderRadius: 2, p: 1, display: 'flex' }}>{React.cloneElement(icon, { sx: { color, fontSize: 28 } })}</Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
      </Box>
      <Typography variant="h5" fontWeight="bold" sx={{ color }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

export default function DashboardRRHH() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get('/api/rrhh/dashboard', { params: { empresa_id: empresaId } });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar dashboard'); }
    finally { setLoading(false); }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const empresaObj = empresas.find(e => e.empresa_id === empresaId) || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1300 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#222' }}>Dashboard RR.HH.</Typography>

        <Box sx={{ mb: 3 }}>
          <Autocomplete
            options={empresas}
            getOptionLabel={o => o?.razon_social || ''}
            value={empresaObj}
            onChange={(_, v) => setEmpresaId(v ? v.empresa_id : '')}
            isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
            renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
            sx={{ minWidth: '16em', maxWidth: 380 }}
          />
        </Box>

        {!empresaId && (
          <Alert severity="info">Selecciona una empresa para ver el dashboard.</Alert>
        )}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {data && !loading && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard icon={<PeopleIcon />} label="Trabajadores Activos"
                  value={data.trabajadores?.activos ?? 0}
                  sub={`Total: ${data.trabajadores?.total ?? 0} | Cesados: ${data.trabajadores?.cesados ?? 0}`}
                  color="#1976d2" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard icon={<AccountBalanceWalletIcon />} label="Última Planilla"
                  value={data.ultima_planilla ? fmt(data.ultima_planilla.total_neto) : '—'}
                  sub={data.ultima_planilla ? `Período ${data.ultima_planilla.periodo} · ${data.ultima_planilla.estado}` : 'Sin planillas'}
                  color="#388e3c" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard icon={<SavingsIcon />} label="CTS Pendiente de Depósito"
                  value={fmt(data.cts_pendiente?.monto)}
                  sub={`${data.cts_pendiente?.total ?? 0} trabajador(es)`}
                  color="#f57c00" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard icon={<CardGiftcardIcon />} label="Gratificaciones Pendientes"
                  value={fmt(data.gratif_pendiente?.monto)}
                  sub={`${data.gratif_pendiente?.total ?? 0} trabajador(es)`}
                  color="#7b1fa2" />
              </Grid>
            </Grid>

            {/* Gráfico planillas */}
            {data.planillas_historial?.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>Evolución Neto a Pagar (últimas planillas)</Typography>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.planillas_historial} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periodo" />
                    <YAxis tickFormatter={v => `S/.${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => fmt(v)} />
                    <Legend />
                    <Bar dataKey="total_neto" name="Neto a pagar" fill="#1976d2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            )}

            {/* Préstamos activos */}
            {data.prestamos?.total > 0 && (
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BeachAccessIcon sx={{ color: '#c62828' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#c62828' }}>Préstamos Activos</Typography>
                  <Chip label={`${data.prestamos.total}`} size="small" color="error" />
                </Box>
                <Typography variant="body2">Saldo total pendiente: <strong>{fmt(data.prestamos.saldo_total)}</strong></Typography>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
